import { FC, forwardRef, useEffect } from 'react';
import { Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DialogProps } from '@toolpad/core/useDialogs';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@components';
import { useArtworkStepperStore } from '../stores/ArtworkStepperStore';
import { CreatePhotoArtworkStepper } from './CreatePhotoArtworkStepper';
import { CreateLogoArtworkStepper } from './CreateLogoArtworkStepper';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      easing={{
        enter: 'cubic-bezier(0.66, 0, 0.34, 1)',
        exit: 'linear',
      }}
      ref={ref}
      {...props}
    />
  );
});

type Props = {
  artworkType: 'PHOTO' | 'LOGO';
};

export const CreateArtworkDialog: FC<DialogProps<Props>> = ({
  payload,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dialogs = useDialogs();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { resetStore } = useArtworkStepperStore();

  useEffect(() => {
    if (open) {
      resetStore();
    }
  }, [open, resetStore]);

  const handleClose = async (confirmClose?: boolean) => {
    if (!confirmClose) {
      resetStore();
      onClose();
      return;
    }
    const closeStatus = await dialogs.open(ConfirmDialog, {
      okText: t('confirm'),
      cancelText: t('cancel'),
      text: t('discardChanges'),
    });

    if (closeStatus === 'confirmed') {
      onClose();
      resetStore();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullWidth={true}
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
      slots={{
        transition: Transition,
      }}
    >
      {payload?.artworkType === 'PHOTO' && (
        <CreatePhotoArtworkStepper onClose={handleClose} />
      )}
      {payload?.artworkType === 'LOGO' && (
        <CreateLogoArtworkStepper onClose={handleClose} />
      )}
    </Dialog>
  );
};
